import { GeneratedLocale } from '@/pages/LessonEditor/types';
import { Autocomplete, Box, Checkbox, FormControl, FormControlLabel, FormGroup, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContextInterface } from '../IContext';

class MatchingContextClass implements MatchingContext {
    type: 'matching';
    themes?: string[];
    locale?: GeneratedLocale;
    generation?: {
        image?: boolean;
    };

    constructor(context: MatchingContext) {
        this.type = context.type;
        this.themes = context.themes;
        this.generation = context.generation;
        this.locale = context.locale;
    }

    isSufficient(): boolean {
        return this.themes !== undefined && this.themes?.length > 0;
    }
}

export interface MatchingContext extends ContextInterface {
    type: 'matching';
    themes?: string[];
    locale?: GeneratedLocale;
    generation?: {
        image?: boolean;
    };
}

export const MatchingPanel = ({ value, onChange }: { value?: MatchingContext; onChange?: (value: MatchingContext) => void }) => {
    const { t } = useTranslation();

    const [themes, setThemes] = useState<string[]>([]);
    const [imageGeneration, setImageGeneration] = useState<boolean>(false);
    const [generatedLocale, setGeneratedLocale] = useState<GeneratedLocale>('zh-Hant');

    return (
        <Stack spacing={2}>
            <Box>
                <Autocomplete
                    multiple
                    groupBy={(option) => option.category}
                    options={[
                        // sample themes
                        { category: t('experiment.generator.question.label.sample'), title: t('experiment.generator.question.sampleTheme.fruit') },
                        { category: t('experiment.generator.question.label.sample'), title: t('experiment.generator.question.sampleTheme.sports') },
                        { category: t('experiment.generator.question.label.sample'), title: t('experiment.generator.question.sampleTheme.animals') },
                        { category: t('experiment.generator.question.label.sample'), title: t('experiment.generator.question.sampleTheme.transport') },
                        { category: t('experiment.generator.question.label.sample'), title: t('experiment.generator.question.sampleTheme.weather') },
                        { category: t('experiment.generator.question.label.sample'), title: t('experiment.generator.question.sampleTheme.food') },
                        { category: t('experiment.generator.question.label.sample'), title: t('experiment.generator.question.sampleTheme.festivals') },
                        { category: t('experiment.generator.question.label.sample'), title: t('experiment.generator.question.sampleTheme.geography') },
                        { category: t('experiment.generator.question.label.sample'), title: t('experiment.generator.question.sampleTheme.technology') },
                        { category: t('experiment.generator.question.label.sample'), title: t('experiment.generator.question.sampleTheme.art') },
                    ]}
                    freeSolo
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={t('experiment.generator.question.placeholder.theme')}
                            required={themes.length === 0}
                            label={t('experiment.generator.question.label.theme')}
                        />
                    )}
                    value={value?.themes?.map((t) => ({ title: t, category: t })) ?? themes.map((t) => ({ title: t, category: t }))}
                    onChange={(_event, value) => {
                        const themes = value.map((v) => (typeof v === 'string' ? v : v.title));
                        setThemes(themes);
                        onChange?.(new MatchingContextClass({ type: 'matching', themes: themes, generation: { image: imageGeneration }, locale: generatedLocale }));
                    }}
                />
            </Box>

            <Box>
                <FormControl fullWidth>
                    <InputLabel>{t('experiment.generator.question.label.languageVariant')}</InputLabel>
                    <Select
                        label={t('experiment.generator.question.label.languageVariant')}
                        fullWidth
                        value={generatedLocale}
                        onChange={(e) => {
                            setGeneratedLocale(e.target.value as GeneratedLocale);
                            onChange?.(new MatchingContextClass({ type: 'matching', themes: themes, generation: { image: imageGeneration }, locale: e.target.value as GeneratedLocale }));
                        }}
                    >
                        <MenuItem value='zh-Hant'>{t('experiment.generator.question.languageVariant.traditional')}</MenuItem>
                        <MenuItem value='zh-Hans'>{t('experiment.generator.question.languageVariant.simplified')}</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <FormGroup sx={{ mt: 1, px: 1 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={value?.generation?.image ?? imageGeneration}
                            onChange={(e) => {
                                setImageGeneration(e.target.checked);
                                onChange?.(new MatchingContextClass({ type: 'matching', themes: themes, generation: { image: e.target.checked }, locale: generatedLocale }));
                            }}
                        />
                    }
                    label={t('experiment.generator.question.label.imageGeneration')}
                />
            </FormGroup>
        </Stack>
    );
};
